import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useMemo, useRef} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {RequestInfo} from "@khan/shared-core";

import TimeTextField from "./time-text-field.tsx";
import {generateTimeSlots} from "./util.ts";

import clockIcon from "@phosphor-icons/core/bold/clock-bold.svg";

/**
 * Default width (in case we don't provide one via props)
 */
const SELECTOR_WIDTH = 224;

/**
 * A formatter for the current time.
 */
const dateFormat = new Intl.DateTimeFormat(RequestInfo.kaLocale, {
    timeStyle: "short", // e.g. "1:30 PM"
});

interface Props {
    /**
     * Whether the dropdown should be disabled.
     */
    disabled?: boolean;
    /**
     * Called when the time value has changed.
     */
    onChange: (value: string) => void;
    /**
     * Provide hints or examples of what to enter.
     */
    placeholder?: string;
    /**
     * Time scale intervals to choose from (represented in minutes).
     * e.g. 15 => pick a time from a 15 mintues range.
     */
    timeScale?: number;
    /**
     * The selected time.
     */
    value?: string | null | undefined;
    /**
     * An optional width for the component.
     */
    width?: number;
    /**
     * An optional test ID for e2e/unit testing
     */
    testId?: string;
    /**
     * An optional ID for pairing with a label
     */
    id?: string;
}

/**
 * A component that allows to pick time by entering the value or selecting it
 * from a list of time slots.
 */
const TimePicker = ({
    disabled,
    onChange,
    placeholder = "",
    timeScale = 15,
    value,
    width = SELECTOR_WIDTH,
    testId,
    id,
}: Props) => {
    /**
     * A reference to the input element.
     */
    const inputRef = useRef<HTMLInputElement | null>(null);

    /**
     * A list of options representing each available time slot
     */
    const timeOptions = useMemo(
        () => generateTimeSlots(timeScale),
        [timeScale],
    );

    const handleSingleSelectChange = (selectedValue: string) => {
        onChange(selectedValue);

        if (inputRef.current) {
            // After the dropdown is closed (by selecting an item or closing by
            // itself), We need to manually focus the input element to return
            // the focus to it.
            inputRef.current.focus();
        }
    };

    const currentTime = dateFormat.format(new Date());

    return (
        <SingleSelect
            id={id}
            autoFocus={false}
            enableTypeAhead={false}
            disabled={disabled}
            onChange={handleSingleSelectChange}
            placeholder={placeholder || t`Choose a time`}
            selectedValue={value}
            style={{width: width}}
            dropdownStyle={styles.dropdown}
            opener={() => {
                return (
                    <View style={styles.opener} testId={testId}>
                        <TimeTextField
                            autoComplete="off"
                            id="time-picker"
                            value={value ?? ""}
                            onChange={onChange}
                            style={styles.input}
                            placeholder={placeholder || t`Choose a time`}
                            disabled={disabled}
                            testId={testId && `${testId}-input`}
                            ref={inputRef}
                        />
                        <PhosphorIcon
                            icon={clockIcon}
                            color={
                                disabled
                                    ? semanticColor.core.foreground.disabled
                                          .default
                                    : semanticColor.core.foreground.instructive
                                          .default
                            }
                            size="small"
                            style={styles.icon}
                        />
                    </View>
                );
            }}
        >
            {[
                <OptionItem
                    label={t`Now`}
                    value={currentTime}
                    key={t`Now`}
                    testId={testId && `${testId}-option-now`}
                />,

                ...timeOptions.map((value: string, index: number) => (
                    <OptionItem
                        label={value}
                        value={value}
                        key={value}
                        testId={testId && `${testId}-option-${value}`}
                    />
                )),
            ]}
        </SingleSelect>
    );
};

const styles = StyleSheet.create({
    opener: {
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "stretch",
    },
    icon: {
        position: "absolute",
        insetInlineEnd: spacing.xSmall_8,
    },
    input: {
        color: semanticColor.core.foreground.neutral.default,
        width: "100%",
    },
    dropdown: {
        justifyContent: "stretch",
        // Ensures the dropdown doesn't overlap the input.
        maxHeight: "50vh",
    },
});

export default TimePicker;
